import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyboardIcon, MicIcon } from 'lucide-react';
import { ThreadPulses } from './ThreadPulses';
import { BeheerMenu } from './BeheerMenu';
import { NoodstopButton } from '../ui/NoodstopButton';
import { RollingCounter } from '../ui/RollingCounter';
import type { AnnaStage } from './useAnnaStage';
interface WallBottomBarProps {
  stage: AnnaStage;
  listening: boolean;
  calm: boolean;
  /** Narrow screens have no satellites, so the agents show as pulses here */
  showThreads: boolean;
  onMic: () => void;
  onType: () => void;
}
const fade = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    duration: 0.3
  }
};

/** Bottom edge, very quiet. Mic and Noodstop are always reachable, even when the wall is calm or asleep. */
export function WallBottomBar({
  stage,
  listening,
  calm,
  showThreads,
  onMic,
  onType
}: WallBottomBarProps) {
  const sleep = stage.view === 'sleep';
  const minimal = sleep || calm;
  const waiting = stage.pending.length;
  return <footer className="pointer-events-none absolute inset-x-0 bottom-0 z-20 grid grid-cols-1 items-end gap-5 px-6 pb-6 md:grid-cols-[1fr_auto_1fr] lg:px-10 lg:pb-8" data-id="element-1560" data-mp="3o437ero92q97">
      <div className="pointer-events-auto hidden min-w-0 md:block" data-id="element-1561" data-mp="x0thhjryp4g">
        <AnimatePresence data-id="element-1562" data-mp="3s3lrxmc5szbi">
          {showThreads && !minimal && <motion.div {...fade} data-id="element-1563" data-mp="34e98zur15cw0">
              <ThreadPulses stopped={stage.stopped} flash={stage.flash} data-id="element-1564" data-mp="34dhlz8bi0nhc" />
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="pointer-events-auto flex items-center justify-center gap-3" data-id="element-1565" data-mp="30al2zgi1qpcv">
        <AnimatePresence data-id="element-1566" data-mp="3e5dnyugpgxqu">
          {!minimal && <motion.button {...fade} type="button" onClick={stage.view === 'approvals' ? stage.leaveApprovals : stage.openApprovals} disabled={waiting === 0 && stage.view !== 'approvals'} className="flex h-14 items-center gap-3 whitespace-nowrap rounded-full border border-white/10 bg-night-900 pl-5 pr-2 text-[22px] text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink disabled:opacity-50" data-id="element-1567" data-mp="39cu9lva71ykz">
              {stage.view === 'approvals' ? 'Terug naar Anna' : 'Wacht op jou'}
              <span className="grid h-10 min-w-[40px] place-items-center rounded-full bg-white/[0.08] px-3 text-[22px] font-semibold text-ink" data-id="element-1568" data-mp="za6pmv6rafjr">
                <RollingCounter value={waiting} data-id="element-1569" data-mp="1nzipemzcb48" />
              </span>
            </motion.button>}
        </AnimatePresence>
        <button type="button" onClick={onMic} aria-pressed={listening} aria-label={listening ? 'Stop met luisteren' : 'Praat met Anna'} className={`grid h-16 w-16 place-items-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${listening ? 'bg-accent text-night-950' : 'border border-white/15 bg-night-900 text-ink hover:border-accent/60'}`} data-id="element-1570" data-mp="6nlz1wy3u7h9">
          <MicIcon className="h-7 w-7" strokeWidth={1.75} data-id="element-1571" data-mp="3blhnm0sngz6d" />
        </button>
        {!minimal && <button type="button" onClick={onType} aria-label="Typ een vraag aan Anna" className="grid h-14 w-14 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1572" data-mp="37kfm1db9gb18">
            <KeyboardIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-1573" data-mp="2sr2nu8yil2hh" />
          </button>}
      </div>

      <div className="pointer-events-auto flex items-center justify-center gap-4 md:justify-end" data-id="element-1574" data-mp="2nwf74w9r5cdg">
        <AnimatePresence data-id="element-1575" data-mp="2jubui4bck1ql">
          {!minimal && <motion.div {...fade} data-id="element-1576" data-mp="2eufojzj3a08e">
              <BeheerMenu data-id="element-1577" data-mp="2evh2cbrhp0tt" />
            </motion.div>}
        </AnimatePresence>
        <NoodstopButton size="wall" onTrigger={stage.emergencyStop} data-id="element-1578" data-mp="i0cj7iy85kdw" />
      </div>
    </footer>;
}